import { call, select, all, takeLatest, fork, put } from 'redux-saga/effects'
import humps from 'humps'
import { SUCCESS, FAILURE } from 'Redux/actions/constants'
import { getToken } from 'Modules/user/data/selectors'
import { closeModal } from 'Modules/modals/actions'
import { fetchEntity } from 'Redux/rootSaga'
import { api } from 'Redux/services'
import { isNilEmpty } from 'Utils'

import { SimpleAlgolia } from 'Utils/algoliaUtils'
import { NOTIFICATION_SUCCESS, NOTIFICATION_ERROR } from 'Modules/notifications/actions'

import {
  CREATE_ALERT, LOAD_ALERTS,
  UPDATE_ALERT, LISTING_ALERT_TYPE,
  BUILDING_ALERT_TYPE, DELETE_ALERT,
  TURN_OFF_ALL_ALERTS,
  API_TURN_OFF_ALL_ALERTS,
  apiTurnOffAllAlertsActions,
  LOAD_LEAD_PROFILE,
  fetchLeadProfileApiActions,
} from './actions'

import {
  apiSaveUserSearch,
  apiUpdateUserSearch,
  getUserSearches,
  apiDeleteUserSearch,
  turnOffAllListingAlerts,
} from './searches/actions'
import {
  createBuildingAlertActions,
  loadBuildingAlertFetchActions,
  updateBuildingAlertActions,
  deleteBuildingAlertActions,
  turnOffAllBuildingAlerts,
} from './buildingAlert/actions'


const algoliaSimpleInstance = new SimpleAlgolia()

export const ALERT_BASE_URL = 'users/alerts'
// shared function which used in building alert
export const createAlert = (fetchFunc) => (function* createAlertFunc(props) {
  const token = yield select(getToken)

  let data = {
    name: props.name,
    alert_frequency: Number(props.frequency),
    offer: props.offer,
    mode: props.mode,
    type: props.type || '',
  }
  // listing alert
  if(props.url) data.url = props.url
  // shared between listing and building alerts
  if(props.user_id) data.user_id = props.user_id
  if(props.cc_list) data.cc_list = props.cc_list
  if(props.beds && props.beds !== -1) data.beds = props.beds
  if(props.price_range) data.price_range = props.price_range
  // building alert
  if(props.building_id) data.building_id = props.building_id
  if(props.tags) {
    data.tags = props.tags
  }
  // if it is create lead mode
  if(props.isNewUser) {
    data = {
      ...data,
      first_name: props.first_name,
      last_name: props.last_name,
      email: props.email,
      phone: props.phone,
      phone_country_code: props.phone_country_code,
      is_new_user: true,
    }
  }
  yield call(fetchFunc, { token, data })
})

const apiSaveAlert = ({ token, data }) => api.callApi(ALERT_BASE_URL, {
  mode: 'POST',
  contentType: 'application/json',
  token,
  data,
})

const getFetchEntity = (fetchAction, apiFunc) => fetchEntity.bind(null, fetchAction, apiFunc)
const getCreateAlertHandler = (fetchAction, apiFunc) => createAlert(getFetchEntity(fetchAction, apiFunc))

// will create alert base on type (ListingAlert, BuildingAlert)
function* createAlertHandler(action) {
  const { payload } = action
  const isListingAlert = payload.type === LISTING_ALERT_TYPE
  const fetchAction = isListingAlert ? apiSaveUserSearch : createBuildingAlertActions
  yield call(getCreateAlertHandler(fetchAction, apiSaveAlert), payload)
}

/**
 * Fetch list of alerts(Listing/Building)
 */

const apiLoadAlerts = ({ token, payload }) => api.callApi(ALERT_BASE_URL, {
  token,
  data: payload,
})

export const loadAlerts = (fetchFunc) => (function* loadAlertsFunc(data) {
  const { leadId } = data
  const payload = {}
  const token = yield select(getToken)
  if(data.type) payload.type = data.type
  if(leadId) payload.user_id = leadId
  if(token) yield call(fetchFunc, { token, payload })
})

const getLoadAlertHandler = (fetchAction, apiFunc) => loadAlerts(getFetchEntity(fetchAction, apiFunc))

function* loadAlertHandler(action) {
  const { payload, forceFetch } = action
  let actions = ''

  const reducerName = humps.camelize(payload.type || '')
  const alertReducer = yield select(state => state[reducerName])
  if(
    (!forceFetch && !payload.leadId) &&
    (
      !alertReducer.isFetching ||
      !isNilEmpty(alertReducer.list)
    )
  ) return null
  if(payload.type === LISTING_ALERT_TYPE) {
    actions = getUserSearches
  } else if(payload.type === BUILDING_ALERT_TYPE) {
    actions = loadBuildingAlertFetchActions
  }
  if(actions) yield call(getLoadAlertHandler(actions, apiLoadAlerts), payload)
}


const apiUpdateAlert = ({ id, payload, token }) => api.callApi(`${ALERT_BASE_URL}/${id}`, {
  mode: 'PATCH',
  contentType: 'application/json',
  token,
  data: payload,
})


function* updateAlertHandler(action) {
  const token = yield select(getToken)
  const { payload, id } = action
  const type = payload.type
  const onSuccess = action.onSuccess || (() => {})
  let fetchActions = ''
  if(type === LISTING_ALERT_TYPE) {
    fetchActions = apiUpdateUserSearch
  } else if(type === BUILDING_ALERT_TYPE) {
    fetchActions = updateBuildingAlertActions
  }
  if(fetchActions) {
    yield call(
      getFetchEntity(fetchActions, apiUpdateAlert),
      { token, payload, id, onSuccess },
    )
  }
}

const apiDeleteAlert = ({ id, token }) => api.callApi(`${ALERT_BASE_URL}/${id}`, {
  mode: 'DELETE',
  contentType: 'application/json',
  token,
})

function* deleteAlertHanlder(action) {
  const token = yield select(getToken)
  const { alertType, id } = action
  let fetchActions = ''
  if(alertType === LISTING_ALERT_TYPE) {
    fetchActions = apiDeleteUserSearch
  } else if(alertType === BUILDING_ALERT_TYPE) {
    fetchActions = deleteBuildingAlertActions
  }
  if(fetchActions) yield call(getFetchEntity(fetchActions, apiDeleteAlert), { token, id })
}

const apiTurnOffAllAlerts = ({ payload, token }) => api.callApi(`${ALERT_BASE_URL}/turn-off`, {
  mode: 'POST',
  contentType: 'application/json',
  token: token || '',
  data: payload,
})
const turnOffAlertEntity = getFetchEntity(apiTurnOffAllAlertsActions, apiTurnOffAllAlerts)

function* turnOffAllAlertsHandler(action) {
  const token = yield select(getToken)
  const payload = {}
  if(action.userId) {
    payload.user_id = action.userId
  }
  yield fork(turnOffAlertEntity, { payload, token })
}
function* turnOffAllAlertsReturnHandler(action) {
  yield put(closeModal())
  if(action.type === API_TURN_OFF_ALL_ALERTS[SUCCESS]) {
    const data = action.response.data.data
    yield put({ type: NOTIFICATION_SUCCESS, msg: data.message })
    yield put(turnOffAllBuildingAlerts())
    yield put(turnOffAllListingAlerts())
  } else {
    yield put({ type: NOTIFICATION_ERROR, msg: 'Fail to turn off all alerts' })
  }
}

// fetching lead profile when url contains lead id
export function* fetchLeadProfile({ leadId }) {
  const token = yield select(getToken)
  if(!token) return null
  const userData = yield select(state => state.userData)
  const self = userData.user
  yield put(fetchLeadProfileApiActions.request())
  const queries = [
    {
      indexName: 'users',
      params: {
        filters: `(id=${leadId}) AND (agent_id=${self.agentId})`,
        attributesToRetrieve: ['id', 'first_name', 'last_name', 'phone', 'email'],
      },
    },
  ]
  const res = yield call(algoliaSimpleInstance.handleSearch, queries)
  const results = res.results
  if(results[0] && results[0].nbHits) {
    const lead = results[0].hits[0]
    yield put(fetchLeadProfileApiActions.success(null, lead))
  }
}

function* watchAlerts() {
  yield all([
    takeLatest(CREATE_ALERT, createAlertHandler),
    takeLatest(LOAD_ALERTS, loadAlertHandler),
    takeLatest(UPDATE_ALERT, updateAlertHandler),
    takeLatest(DELETE_ALERT, deleteAlertHanlder),
    takeLatest(TURN_OFF_ALL_ALERTS, turnOffAllAlertsHandler),
    takeLatest(LOAD_LEAD_PROFILE, fetchLeadProfile),
    takeLatest([API_TURN_OFF_ALL_ALERTS[SUCCESS], API_TURN_OFF_ALL_ALERTS[FAILURE]], turnOffAllAlertsReturnHandler),
  ])
}


export default watchAlerts
